import React, { useState } from 'react';
import MDMCompanyCard from 'components/Company/MDMCompanyCard';
import MDMCreditCard from 'components/Company/MDMCreditCard';
import BusinessUnitCard from 'components/Company/BusinessUnitCard';
import SwitchToOFMPModal from 'components/Company/Modals/SwitchToOFMPModal';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import Modal from 'components/UI/Modal';
import { Space } from 'styles/themeSpaces';
import { MDMGeneralTabProps } from 'types/credit.types';
import {
  CREDIT_MODAL_TITLES,
  CREDIT_BUTTONS,
  CREDIT_SOURCES,
  CREDIT_DEFAULTS
} from 'constants/credit';
import { useAuth } from 'lib/Auth';
import { Main } from 'styles/themeColors';
import { hasOfmpAdminProfile } from 'data/User';


const MDMGeneralTab: React.FC<MDMGeneralTabProps> = ({
  selectedBusinessUnit,
  mdmId,
  creditId,
  companyId,
  isConnected,
  creditInfo,
  vendorCompanyDetails,
  creditTrackingMode,
  mdmApplyReq,
  updateCreditReq,
  createCreditReq,
  isInvalid,
  setIsInvalid,
  error,
  vendorsList,
  initialVendor,
  initialBusinessUnit,
  hasActiveSubscriptions,
  featureFlags
}) => {
  const [showSwitchModal, setShowSwitchModal] = useState(false);
  const { user } = useAuth();

  const isOfmpAdmin = hasOfmpAdminProfile(user);

  const sellerCode = selectedBusinessUnit?.code;

  const onApplyMDM = (value: string) => {
    if (sellerCode && value) {
      const userEmail = user?.email;
      const source = CREDIT_SOURCES.MDM;
      if (!creditId) {
        mdmApplyReq?.run(sellerCode, value, companyId, source, selectedBusinessUnit?.id, vendorCompanyDetails.id);
      } else {
        updateCreditReq?.run(creditId, {
          buyerExternalCode: value,
          source,
          updatedBy: userEmail,
          sellerCode
        });
      }
    }
  };

  const onUpdateCredit = (value: string) => {
    const request = {
      buyerExternalCode: companyId,
      availableAmount: value,
      source: CREDIT_SOURCES.INTERNAL,
      updatedBy: user?.email
    };

    updateCreditReq?.run(creditId, request);
  };

  const onTrackManuallyClick = () => {
    setShowSwitchModal(true);
  };

  const onConfirmSwitchToOFMP = () => {
    setShowSwitchModal(false);

    if (creditId) {
      // If credit record exists, update it
      const request = {
        buyerExternalCode: companyId,
        availableAmount: CREDIT_DEFAULTS.INITIAL_AMOUNT,
        reservedAmount: CREDIT_DEFAULTS.RESERVED_AMOUNT,
        usedAmount: CREDIT_DEFAULTS.USED_AMOUNT,
        allowNegativeBalance: CREDIT_DEFAULTS.ALLOW_NEGATIVE,
        source: CREDIT_SOURCES.INTERNAL,
      };
      updateCreditReq?.run(creditId, request);
    } else {
      // If credit record doesn't exist, create it internally
      if (sellerCode && selectedBusinessUnit) {
        createCreditReq?.run(sellerCode, companyId, companyId, CREDIT_SOURCES.INTERNAL, selectedBusinessUnit.id, vendorCompanyDetails.id);
      }
    }
  };

  const onCancelSwitchToOFMP = () => {
    setShowSwitchModal(false);
  };

  return (
    <FlexboxContainer flexDirection='column' gap={Space.spHalf}>
      <FlexboxContainer justifyContent="flex-start" margin={`${Space.sp2x} 0 0 0`} width='100%' gap={Space.spHalf}>
        <MDMCompanyCard
          mdmId={mdmId}
          onApplyMDM={onApplyMDM}
          isInvalid={isInvalid}
          isConnected={isConnected}
          setIsInvalid={setIsInvalid}
          creditTrackingMode={creditTrackingMode}
          creditInfo={creditInfo}
          error={error}
        />

        <MDMCreditCard
          isConnected={isConnected}
          creditInfo={creditInfo}
          onUpdateCredit={onUpdateCredit}
          onTrackManuallyClick={onTrackManuallyClick}
          creditTrackingMode={creditTrackingMode}
          vendorCompanyDetails={vendorCompanyDetails}
        />

        <Modal
          title={CREDIT_MODAL_TITLES.SWITCH_TO_OFMP}
          onClose={onCancelSwitchToOFMP}
          showModal={showSwitchModal}
          theme="accent"
          onClick={onConfirmSwitchToOFMP}
          primaryButtonText={CREDIT_BUTTONS.CONFIRM}
          showGhostButton={true}
          ghostButtonText={CREDIT_BUTTONS.CANCEL}
          onGhostButtonClick={onCancelSwitchToOFMP}
          primaryButtonColor={Main.brandDark}
          color={Main.darkGray}
          borderColor={Main.lightGray}
        >
          <SwitchToOFMPModal />
        </Modal>
      </FlexboxContainer>
      {
        isOfmpAdmin && 
        featureFlags?.subscriptionProduct?.enabled && 
        <FlexboxContainer>
          <BusinessUnitCard
            companyId={companyId}
            vendorsList={vendorsList!}
            initialVendor={initialVendor}
            initialBusinessUnit={initialBusinessUnit}
            hasActiveSubscriptions={hasActiveSubscriptions}
          />
        </FlexboxContainer>
      }
    </FlexboxContainer>
  );
};

export default MDMGeneralTab;